<template>
  <!-- Solo renderizar el gráfico cuando los datos estén disponibles -->
  <VChart
    v-if="permissionsByRolesOption"
    class="chart"
    :option="permissionsByRolesOption"
    autoresize
  />
</template>

<script setup>
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart, BarChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DatasetComponent,
} from "echarts/components";
import VChart, { THEME_KEY } from "vue-echarts";
import { ref, provide, onMounted, computed, watch } from "vue";
import { useStore } from "vuex";

// Registrar las librerías y componentes necesarios
use([
  CanvasRenderer,
  PieChart,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DatasetComponent,
]);

// Proveer el tema para el gráfico
provide(THEME_KEY, "ovilia-green");

const store = useStore();
const permissionsByRolesOption = ref(null); // Inicialmente vacío

onMounted(() => {
  getRoles();
});

const getRoles = async () => {
  try {
    await store.dispatch("roles/getAllRoles", "?includePermissions=true");
  } catch (error) {
    console.error("Error loading roles:", error);
  }
};

// Computar los registros cuando estén disponibles
const records = computed(() => store.state.roles.allRolesResponse);

// Actualizar el gráfico cuando los datos estén disponibles
watch(records, (newRecords) => {
  if (newRecords?.data) {
    const categories = newRecords.data.map((e) => e.name);
    const data = newRecords.data.map((e) => e.permissions?.length || 0);

    // Función para generar una lista de colores dinámicamente
    const generateColors = (length) => {
      const colors = [];
      for (let i = 0; i < length; i++) {
        colors.push(`hsl(${(i * 360) / length}, 70%, 50%)`);
      }
      return colors;
    };

    // Actualizar las opciones del gráfico
    permissionsByRolesOption.value = {
      title: {
        text: "Permissions by Role",
        subtext: "The number of permissions assigned to each role.",
      },
      tooltip: {
        trigger: "item",
      },
      xAxis: {
        type: "category",
        data: categories,
        axisLabel: {
          rotate: 45, // Rotar las etiquetas del eje X 45 grados
          interval: 0, // Mostrar todas las etiquetas
          align: "right", // Alinear las etiquetas
        },
      },
      yAxis: {
        type: "value",
      },
      series: [
        {
          data: data,
          type: "bar",
          itemStyle: {
            color: function (params) {
              // Generar colores de acuerdo a la cantidad de datos
              const colorList = generateColors(data.length);
              return colorList[params.dataIndex];
            },
          },
          label: {
            show: true, // Mostrar las etiquetas
            position: "top", // Posición de las etiquetas arriba de las barras
            formatter: "{c}", // Mostrar el valor (cantidad de permisos)
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    };
  }
});
</script>

<style scoped>
.chart {
  height: 400px;
  width: 800px;
}
</style>
